import React, { Fragment, useMemo, useState } from "react";
import flatten from "lodash/flatten";
import { useLangContext } from "@layouts/home/LangContext";
import { VisitQuestionnaireAnswer } from "@pages/ecoa/visit/VisitQuestionnaire";
import { CoStringSpec, CrfCheckItemSpec, CrfItemCommonDescription, CrfSingleSelectItemSpec, StudySpec } from "maven-lib/dist/consts/edc";
import { GetEcoaCapturesResponse } from "maven-lib/dist/dto/ecoaCapture";
import { EcoaVisitQuestionnaireData } from "maven-lib/dist/dto/ecoaVisit";
import { EcoaCrf, EcoaCrfStatus } from "maven-lib/dist/dto/entity/ecoaCrf";
import { EcoaQuestionnaire } from "maven-lib/dist/dto/entity/ecoaVisit";
import { StudyCaptureData } from "maven-lib/dist/dto/entity/studyCapture";
import { VisitQuestionnaireFooter } from "./VisitQuestionnaireFooter";
import { VisitQuestionnaireItem } from "./VisitQuestionnaireItem";
import { RenderedQuestion } from "./VisitQuestionnaireWrapper";
import { makeCaptureData } from "../../../lib";
import { HbDivider } from "@maven-surface/components/base/HbDivider";
import { HbParagraph } from "@maven-surface/components/base/HbParagraph";

interface VisitQuestionnaireMultipleItemInnerProps {
  studySpec: StudySpec;
  crfs: Array<EcoaCrf>;
  folderId: string;
  visitId: string;
  visitSeq: number;
  crfPageId: string;
  crfPageSeq: number;
  questionnaires: Array<EcoaQuestionnaire>;
  questionnairesData: EcoaVisitQuestionnaireData;
  questionnaireSeq: number;
  captureResponse: GetEcoaCapturesResponse | null;
  questionnaireAnswersState: [Array<VisitQuestionnaireAnswer>, React.Dispatch<React.SetStateAction<VisitQuestionnaireAnswer[]>>];
  isAvailableItem: (questionnaire: EcoaQuestionnaire | null, answers: Array<VisitQuestionnaireAnswer>) => boolean;
  getItemsFromCapturedData: (captureData: GetEcoaCapturesResponse, questionnaire: EcoaQuestionnaire) => VisitQuestionnaireAnswer[];
}

export function VisitQuestionnaireMultipleItemInner({
  studySpec,
  crfs,
  folderId,
  visitId,
  visitSeq,
  crfPageId,
  crfPageSeq,
  questionnaireSeq,
  questionnairesData,
  questionnaires,
  captureResponse,
  questionnaireAnswersState,
  isAvailableItem,
  getItemsFromCapturedData,
}: VisitQuestionnaireMultipleItemInnerProps) {
  const { fontSize } = useLangContext();
  const capturedItems = useMemo(() => {
    if (captureResponse) {
      const mergedItems: Array<Array<VisitQuestionnaireAnswer>> = [];
      if (!captureResponse) return mergedItems;

      questionnaires.forEach((questionnaire) => {
        mergedItems.push(getItemsFromCapturedData(captureResponse, questionnaire));
      });

      return mergedItems;
    }

    const initialCapturedItems = (() => {
      const mergedItems: Array<Array<{ itemId: string | null; dbVal: string | number | null }>> = [];
      questionnaires.forEach((questionnaire) => {
        if (questionnaire.item.original.type === "CHECK") {
          const checkItem: CrfCheckItemSpec = questionnaire.item.original;
          const items = checkItem.itemCode.codes.map(({ id: itemId }) => ({ itemId, dbVal: null }));
          const typeTextItems = checkItem.itemCode.codes
            .map(({ typeText }) => ({ itemId: typeText?.id || "", dbVal: null }))
            .filter(({ itemId }) => itemId !== "");

          mergedItems.push([...items, ...typeTextItems]);
        } else if (questionnaire.item.original.type === "SINGLE_SELECT") {
          const singleSelectItem: CrfSingleSelectItemSpec = questionnaire.item.original;
          const typeTextItems = singleSelectItem.itemCode.codes
            .map(({ typeText }) => ({ itemId: typeText?.id || "", dbVal: null }))
            .filter(({ itemId }) => itemId !== "");

          mergedItems.push([{ itemId: questionnaire.item.id, dbVal: null }, ...typeTextItems]);
        } else if (questionnaire.item.original.type === "DATE") {
          const dateValues: Array<{ itemId: string | null; dbVal: string | number | null }> = [
            { itemId: questionnaire.item.id, dbVal: null },
          ];

          if (questionnaire.item.original.children?.items.length) {
            questionnaire.item.original.children?.items.forEach((childrenItem) => {
              if (childrenItem.type === "TIME") {
                // 현재 TIME 아이템만 date questionnaire item 내부에서 구현 되어 있음
                dateValues.push({ itemId: childrenItem.id, dbVal: null });
              }
            });
          }
          mergedItems.push(dateValues);
        } else {
          mergedItems.push([{ itemId: questionnaire.item.id, dbVal: null }]);
        }
      });
      return mergedItems;
    })();

    return initialCapturedItems;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureResponse, questionnaires]);

  const [answers, setAnswers] = questionnaireAnswersState;

  const [disabledFooter, setDisabledFooter] = useState<boolean>(false);
  const crf = crfs.find((c) => c.folderId === folderId && c.visitId === visitId && c.visitSeq === visitSeq && c.crfPageId === crfPageId);

  const getCurrentItemValues = (itemId: string) =>
    capturedItems
      .find((capturedItem) => capturedItem.find((item) => item.itemId === itemId))
      ?.map(({ itemId }) => ({ itemId, dbVal: answers.find((answer) => answer.itemId === itemId)?.dbVal ?? null }));

  const handleValueChange = (itemValues: Array<{ itemId: string | null; dbVal: string | number | null }>): void => {
    setAnswers((prev) => {
      const clonePrev = [...prev];
      itemValues.forEach((v) => {
        const oldIdx = clonePrev.findIndex((p) => p.itemId === v.itemId);
        oldIdx > -1 ? (clonePrev[oldIdx] = v) : clonePrev.push(v);
      });

      return clonePrev;
    });
  };

  const getPayloadsAll = (
    capturedItemValues: Array<{
      itemId: string | null;
      dbVal: string | number | null;
    }>,
    questionnaire: EcoaQuestionnaire
  ): Array<StudyCaptureData | null> | null => {
    if (questionnaire.item.original.type === "CONSTANT") return null; // TODO: CONSTANT에 대한 캡쳐 처리가 추가되면 수정 예정

    const typeTextItemIds: Array<string> = [];

    if (questionnaire.item.original.type === "SINGLE_SELECT") {
      questionnaire.item.original.itemCode.codes.forEach((code) => {
        if (code.typeText?.id && answers.some(({ dbVal, itemId }) => itemId === code.typeText?.id && dbVal === code.dbVal)) {
          typeTextItemIds.push(code.typeText.id);
        }
      });
    } else if (questionnaire.item.original.type === "CHECK") {
      questionnaire.item.original.itemCode.codes.forEach((code) => {
        if (code.typeText?.id && answers.some(({ dbVal, itemId }) => itemId === code.id && (dbVal || typeof dbVal === "number"))) {
          typeTextItemIds.push(code.typeText.id);
        }
      });
    }

    const payloads: Array<StudyCaptureData | null> = capturedItemValues
      .map<StudyCaptureData | null>((capturedItem) => {
        const answer = answers.find((ans) => ans.itemId === capturedItem.itemId) || { itemId: null, dbVal: null };
        if (!answer.itemId) return null;

        if (typeTextItemIds?.some((typeTextItemId) => typeTextItemId === answer.itemId) && (answer.dbVal === null || answer.dbVal === "")) {
          return null;
        } else if (answer.dbVal || typeof answer.dbVal === "number") {
          // valid answer dbVal
          return makeCaptureData(questionnaire.item, answer, capturedItem.dbVal, folderId, visitId, visitSeq, crfPageId, crfPageSeq);
        } else {
          // invalid answer dbVal
          if (capturedItem.dbVal || typeof capturedItem.dbVal === "number") {
            // update dbVal to null
            return makeCaptureData(questionnaire.item, answer, null, folderId, visitId, visitSeq, crfPageId, crfPageSeq);
          }
        }

        return null;
      })
      .filter((e) => e);

    if (!payloads.length) return null;
    else if (payloads.every((e) => e?.val.dbVal === null || e?.val.dbVal === "") && answers.length === payloads.length) return null;
    else if (typeTextItemIds.length && !typeTextItemIds.every((id) => payloads.find((e) => e?.id.itemId === id))) return null;
    return payloads;
  };

  const handleDisabledFooterChange = (val: boolean) => {
    setDisabledFooter(val);
  };

  const isDisableFooterButtonByAvailability = (availableQuestionnaires: Array<EcoaQuestionnaire>): boolean =>
    availableQuestionnaires.some((questionnaire) => {
      if (questionnaire.item.original.type === "CHECK") {
        if (questionnaire.item.original.itemCode.codes.length > 1) {
          let hasTypeTextItemAndTyped = true;
          let invalidItemAll = true;

          questionnaire.item.original.itemCode.codes.forEach((code) => {
            const foundItemDbVal = answers.find((answer) => answer.itemId === code.id)?.dbVal;

            if (
              (foundItemDbVal || typeof foundItemDbVal === "number") &&
              code?.typeText?.id &&
              !answers.find((answer) => answer.itemId === code.typeText?.id)?.dbVal
            ) {
              hasTypeTextItemAndTyped = false;
            }
            if (invalidItemAll && (foundItemDbVal || typeof foundItemDbVal === "number")) invalidItemAll = false;
          });

          return !hasTypeTextItemAndTyped || invalidItemAll;
        } else {
          return false;
        }
      } else if (questionnaire.item.original.type === "DATE") {
        const isValidDateDbVal = !!answers.find((answer) => answer?.itemId === questionnaire.item.id)?.dbVal;
        const isValidChild: boolean =
          !questionnaire.item.original.children?.items?.length ||
          questionnaire.item.original.children.items.every(
            (item) => answers.find((answer) => item.type === "TIME" && answer.itemId === item.id)?.dbVal
          );

        return !(isValidDateDbVal && isValidChild);
      } else if (questionnaire.item.original.type === "CONSTANT") {
        // TODO: CONSTANT에 대한 처리가 추가되면 삭제 예정
        return false;
      } else {
        const foundItemDbVal = answers.find((answer) => answer?.itemId === questionnaire.item.id)?.dbVal;
        return !(foundItemDbVal || typeof foundItemDbVal === "number"); // 입력값 0에 대한 대응
      }
    });

  const isAvailableQuestionnairesAll = (availableQuestionnaires: Array<EcoaQuestionnaire>): boolean =>
    crf?.status !== EcoaCrfStatus.NotAvailable && (!!availableQuestionnaires.length || answers.some((answer) => answer.dbVal !== null));

  const getFooterButtonType = (availableQuestionnairesAll: boolean, isLastQuestionnaire: boolean) => {
    if ([EcoaCrfStatus.Ongoing, EcoaCrfStatus.NotDone].includes(crf?.status!)) {
      if (!captureResponse) {
        return "EMPTY";
      } else {
        if (availableQuestionnairesAll || isLastQuestionnaire) {
          return "CAPTURE";
        } else {
          return "NO_CAPTURE";
        }
      }
    }
    return "NO_CAPTURE";
  };

  // NOTE: 화면 전체가 비활성화 되어있는 상태로 submit을 하는 상황에서 capture 없이 다음을 진행할 수 없기 때문에 임의로 null을 전달
  const getCaptureParams = (available: boolean) => {
    return available
      ? flatten(capturedItems.map((val, valIdx) => getPayloadsAll(val, questionnaires[valIdx]))).filter((data) => data)
      : flatten(
          capturedItems.map((_, valIdx) =>
            makeCaptureData(
              questionnaires[valIdx].item,
              { itemId: questionnaires[valIdx]?.item?.id, dbVal: null },
              null,
              folderId,
              visitId,
              visitSeq,
              crfPageId,
              crfPageSeq
            )
          )
        );
  };

  const isShowPopup = questionnaires[0].item?.original?.ecoa?.popup || false;
  const isLastQuestionnaire = questionnaireSeq === questionnairesData.questionnaires.length;

  const availableQuestionnaires: Array<EcoaQuestionnaire> = [];

  const flattenCapturedItems = flatten(capturedItems);

  const questionnairesCurrentItemValues = questionnaires.map((questionnaire) => getCurrentItemValues(questionnaire.item.id) || []);

  const flattenQuestionnairesCurrentItemValues = flatten(questionnairesCurrentItemValues);

  return (
    <>
      {questionnaires.map((questionnaire, questionnaireIdx) => {
        const available = isAvailableItem(questionnaire, answers);
        available && availableQuestionnaires.push(questionnaire);

        const descriptionText = (questionnaire?.item?.original?.description as CoStringSpec)?.text || "";
        const descriptionPosition = (questionnaire?.item?.original?.description as CrfItemCommonDescription)?.ecoa?.position || "BOTTOM";
        const descriptionColor = ((color: any) =>
          color === "RED" ? "DANGER" : color === "GRAY" ? "GRAY" : color === "BLUE" ? "PRIMARY" : undefined)(
          (questionnaire?.item?.original?.description as CoStringSpec)?.color
        );
        const descriptionSize = fontSize === "LARGE" ? "5X-LARGE" : fontSize === "MEDIUM" ? "3X-LARGE" : "LARGE";
        const textSize = fontSize === "LARGE" ? "5X-LARGE" : fontSize === "MEDIUM" ? "3X-LARGE" : "X-LARGE";
        const question = (() => {
          if (
            (questionnaire?.item.original.type === "SINGLE_SELECT" ||
              questionnaire?.item.original.type === "TIME" ||
              questionnaire?.item.original.type === "TEXT" ||
              questionnaire?.item.original.type === "AUTO_TEXT" ||
              questionnaire?.item.original.type === "SLIDER") &&
            questionnaire.item.original.ecoa?.label
          ) {
            return questionnaire.item.original.ecoa.label;
          }
          return questionnaire?.question;
        })();
        const questionAlign =
          (questionnaire?.item?.original.type === "SINGLE_SELECT" ||
            questionnaire?.item?.original.type === "TIME" ||
            questionnaire?.item?.original.type === "TEXT") &&
          questionnaire?.item?.original.align
            ? questionnaire?.item?.original.align
            : "LEFT";

        return (
          <Fragment key={questionnaireIdx}>
            {descriptionText && descriptionPosition === "TOP" && (
              <>
                <HbParagraph align={"LEFT"} size={descriptionSize} color={descriptionColor} bold={false}>
                  {descriptionText}
                </HbParagraph>
                <HbDivider />
              </>
            )}
            {question && (
              <RenderedQuestion
                question={question}
                fontSize={fontSize}
                questionBold={true}
                icon={questionnaire.item.original?.labelIcon}
                questionAlign={questionAlign}
              />
            )}
            {questionnaire.text && <HbParagraph text={questionnaire.text} size={textSize} bold={true} />}
            <HbDivider />
            <VisitQuestionnaireItem
              studySpec={studySpec}
              folderId={folderId}
              visitId={visitId}
              visitSeq={visitSeq}
              crfPageId={crfPageId}
              crfPageSeq={crfPageSeq}
              questionnaire={questionnaire}
              defaultItemValues={getCurrentItemValues(questionnaire.item.id)}
              capturedItems={flattenCapturedItems}
              disabled={crf?.status === EcoaCrfStatus.NotAvailable || !captureResponse || crf?.status === EcoaCrfStatus.Done || !available}
              isAvailable={available}
              onValueChange={handleValueChange}
              onChangeDisabledFooter={handleDisabledFooterChange}
              captureResponse={captureResponse}
              flattenQuestionnairesCurrentItemValues={flattenQuestionnairesCurrentItemValues}
            />
            <HbDivider />
            {descriptionText && descriptionPosition === "BOTTOM" && (
              <HbParagraph align={"LEFT"} size={descriptionSize} color={descriptionColor} bold={false}>
                {descriptionText}
              </HbParagraph>
            )}
          </Fragment>
        );
      })}
      <VisitQuestionnaireFooter
        buttonType={getFooterButtonType(isAvailableQuestionnairesAll(availableQuestionnaires), isLastQuestionnaire)}
        disabled={
          disabledFooter ||
          isDisableFooterButtonByAvailability(
            availableQuestionnaires.filter((availableQuestionnaire) => availableQuestionnaire.item.original.type !== "DESCRIPTION")
          )
        }
        isLastQuestionnaire={isLastQuestionnaire}
        progressBarValue={((questionnaireSeq - 1) / questionnairesData.questionnaires.length) * 100.0}
        crfStatus={crf!.status}
        folderId={folderId}
        visitId={visitId}
        visitSeq={visitSeq}
        crfPageId={crfPageId}
        crfPageSeq={crfPageSeq}
        getCaptureData={() => getCaptureParams(isAvailableQuestionnairesAll(availableQuestionnaires))}
        questionnairesData={questionnairesData}
        questionnaireSeq={questionnaireSeq}
        isShowPopup={isShowPopup}
        submitConfirmPopup={studySpec.config?.ecoa?.submitConfirmPopup}
      />
    </>
  );
}
